import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * The rendered width of an element, kept current as it resizes. Returns a callback ref to put on
 * the element, and its width: unrounded (callers floor it where a sub-pixel overshoot matters),
 * null until measured, and null for good where ResizeObserver does not exist (jsdom), so a
 * caller's "unknown width" branch is also what tests exercise. A zero width (the element is not
 * laid out, e.g. hidden) is not taken as a measurement.
 *
 * A callback ref rather than a ref object, because the element often mounts after the first
 * render (behind a loading state), and an effect keyed on a ref object never sees it arrive.
 */
/**
 * The same content-box width ResizeObserver reports, read synchronously. The bounding box
 * includes borders, padding and any scrollbar or reserved gutter, which contentRect does not; the
 * two have to agree or the first frame is drawn at a slightly different scale from every later one.
 */
const contentWidth = (element: HTMLElement): number => {
  const style = getComputedStyle(element);
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  // Borders plus the vertical scrollbar or its reserved gutter.
  const frame = element.offsetWidth - element.clientWidth;
  return element.getBoundingClientRect().width - frame - padding;
};

export const useElementWidth = <T extends HTMLElement>(): [(node: T | null) => void, number | null] => {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  // Measured synchronously as the element attaches, before the browser paints, so the first
  // frame already has the width rather than a frame at the unmeasured fallback.
  useLayoutEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return;
    const measured = contentWidth(element);
    if (measured > 0) setWidth(measured);
  }, [element]);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next != null && next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [setElement, width];
};
